/**
 * The pieces both challenge pages show.
 *
 * The two modes -- a run on the student's machine, a session on ours -- diverge completely in
 * how a student sets up and submits, and are maintained as separate pages for that reason. What
 * they have in common is what a challenge *is*: its deadline, its thresholds, its leaderboard,
 * and the student's own attempts. Copying those into both pages would mean two places to fix a
 * leaderboard bug, so they live here and the pages compose them.
 */

import { Trophy } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { GoalBadge } from "@/components/goal-badge";
import { ScoreCell } from "@/components/score-cell";
import { SubmissionsTable } from "@/components/submissions-table";
import type { AsyncState } from "@/hooks/use-async";
import type { Challenge, Submission } from "@/lib/api";
import { formatSteps } from "@/lib/format";
import { submissionHref } from "@/lib/links";
import { bestRun } from "@/lib/scoring";
import { cn } from "@/lib/utils";

export function Tile({
  icon,
  label,
  children,
  tone,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
  tone?: "danger";
}) {
  return (
    <div className="rounded-lg border px-3.5 py-3">
      <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
        {icon}
        {label}
      </p>
      <p
        className={cn(
          "mt-1 text-sm font-medium",
          tone === "danger" && "text-destructive",
        )}
      >
        {children}
      </p>
    </div>
  );
}

/** The signed-in student's best run, so they can see where they stand. */
export function BestRunTile({
  challenge,
  submissions,
}: {
  challenge: Challenge;
  submissions: Submission[] | null;
}) {
  if (!submissions) return null;
  // Only scored runs can have a best. The list now includes failures, whose
  // metrics are null, and sorting those in would put an unjudged attempt on top.
  //
  // "Best" is the leaderboard's word: tier first, then steps, then error. This used to
  // sort on steps alone, so a 160-step run that missed the goal outranked a 1,012-step
  // perfect one here while the leaderboard, correctly, said the opposite.
  const best = bestRun(submissions);
  if (!best) {
    return (
      <Tile icon={<Trophy className="size-3.5" aria-hidden />} label="Your best">
        <span className="text-muted-foreground">
          {submissions.length ? "No scored runs yet" : "No runs yet"}
        </span>
      </Tile>
    );
  }
  return (
    <Tile icon={<Trophy className="size-3.5" aria-hidden />} label="Your best">
      <Link
        to={submissionHref(challenge.id, best)}
        className="flex items-center gap-2 underline-offset-4 hover:underline"
      >
        <GoalBadge tier={best.goal_tier} />
        <ScoreCell points={best.score?.points ?? null} max={challenge.rubric_spec.max_points} className="text-xs" />
        <span className="text-muted-foreground font-mono text-xs">
          {formatSteps(best.nav_steps)} steps
        </span>
      </Link>
    </Tile>
  );
}

/**
 * The caller's attempts at one challenge.
 *
 * Takes the fetched state rather than fetching: the shell already loads the same list for
 * the "Your best" tile, and this component used to fire the identical request a second
 * time on every page view.
 */
export function MySubmissionsTable({
  challenge,
  state,
}: {
  challenge: Challenge;
  state: AsyncState<Submission[]>;
}) {
  return (
    <AsyncBoundary state={state} empty={<EmptyState title="No submissions yet" />}>
      {(rows) => <SubmissionsTable challenge={challenge} submissions={rows} />}
    </AsyncBoundary>
  );
}
